import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import * as Icons from "lucide-react";
import { SOFTWARE, SOFTWARE_CATEGORIES } from "@/lib/data";
import { Reveal, Cascade } from "@/components/Reveal";
import { useDetailModal } from "@/components/DetailModalContext";

const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp className={className} strokeWidth={1.5} />;
};

export default function Software() {
  const [active, setActive] = useState("all");
  const { openDetail } = useDetailModal();
  const items =
    active === "all" ? SOFTWARE : SOFTWARE.filter((s) => s.category === active);

  return (
    <section id="yazilimlar" className="scroll-mt-24" data-testid="software">
      <div className="container-shell">
        <Reveal className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <p className="overline">Ekstra Yazılım Çözümleri</p>
            <h2 className="heading-lg mt-4">
              <Cascade text="Her sektöre özel {akıllı} yazılımlar." />
            </h2>
          </div>
        </Reveal>

        {/* Category pills */}
        <Reveal className="mt-10 flex flex-wrap gap-2.5" data-testid="software-filters">
          {SOFTWARE_CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setActive(c.key)}
              className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-[background-color,color,border-color] duration-300 ${
                active === c.key
                  ? "border-orange bg-orange text-white"
                  : "border-ink/10 bg-white text-ink/70 hover:border-ink/25"
              }`}
              data-testid={`filter-${c.key}`}
            >
              {c.label}
            </button>
          ))}
        </Reveal>

        {/* Cards */}
        <motion.div layout className="mt-[48px] grid grid-cols-1 gap-[32px] md:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {items.map((s) => (
              <motion.div
                key={s.title}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="group cursor-pointer overflow-hidden rounded-2xl border border-ink/5 bg-white shadow-[0_2px_10px_rgba(32,32,32,0.04)] transition-shadow duration-500 hover:shadow-[0_24px_50px_rgba(32,32,32,0.10)]"
                onClick={() => openDetail(s)}
                data-testid={`software-card-${s.title}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-ink backdrop-blur-sm">
                    {s.tag}
                  </span>
                </div>
                <div className="p-7">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange/10 text-orange">
                    <Icon name={s.icon} className="h-5.5 w-5.5" />
                  </span>
                  <h3 className="mt-4 text-lg font-bold tracking-tight text-ink">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/60">{s.desc}</p>
                  <button
                    onClick={() => openDetail(s)}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-ink transition-colors hover:text-orange"
                    data-testid={`software-detail-${s.title}`}
                  >
                    Teklif Al
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
